import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, ArrowRight, X } from "lucide-react";

const storageKey = "prepnexis:billing-reminder:";

export default function BillingReminder({ onHeightChange }) {
  const [reminder, setReminder] = useState(null);
  const [dismissedId, setDismissedId] = useState(null);
  const bannerRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    let pending = false;
    const refresh = async () => {
      if (pending || document.visibilityState === "hidden") return;
      pending = true;
      try {
        const response = await fetch("/api/current-subscription", {
          credentials: "include", signal: controller.signal,
        });
        if (!response.ok) return;
        const data = await response.json();
        const next = data.reminder || null;
        setReminder(next);
        try {
          if (next && localStorage.getItem(storageKey + next.id)) setDismissedId(next.id);
        } catch { /* The banner still closes when browser storage is unavailable. */ }
      } catch { /* Keep the dashboard usable during a temporary billing outage. */ }
      finally { pending = false; }
    };
    refresh();
    const timer = setInterval(refresh, 60 * 1000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      controller.abort();
      clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, []);

  const visible = reminder && reminder.id !== dismissedId;
  useEffect(() => {
    if (!visible || !bannerRef.current) { onHeightChange(0); return undefined; }
    const element = bannerRef.current;
    const measure = () => onHeightChange(element.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => { observer.disconnect(); onHeightChange(0); };
  }, [visible, onHeightChange]);

  if (!visible) return null;
  const ending = reminder.type === "ending";
  const formattedDate = new Date(reminder.date).toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: "numeric",
  });
  const dismiss = () => {
    setDismissedId(reminder.id);
    try { localStorage.setItem(storageKey + reminder.id, "dismissed"); } catch { /* Optional persistence. */ }
  };

  return (
    <div ref={bannerRef} role="status" className="border-b border-purple-200 bg-purple-50 px-4 py-3 text-gray-700 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-7xl items-start gap-3 sm:items-center">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-white text-purple-700 shadow-sm">
          <CalendarClock className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-5">
          <div>
            <p className="text-sm font-bold text-purple-900">
              {ending ? `Your ${reminder.plan} access ends on ${formattedDate}` : `Your ${reminder.plan} plan renews on ${formattedDate}`}
            </p>
            <p className="mt-0.5 text-xs leading-5 text-purple-800">
              {ending ? "Renewal is cancelled. Keep practising with your plan until then." : "Your next billing date is coming up. Review your plan before it renews."}
            </p>
          </div>
          <Link to="/billing" className="inline-flex min-h-9 shrink-0 items-center gap-2 self-start rounded-md !bg-purple-700 px-3 py-2 text-xs font-bold !text-white shadow-sm transition hover:!bg-purple-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-700 sm:self-center">
            Manage billing <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
        </div>
        <button type="button" aria-label="Dismiss billing reminder" onClick={dismiss} className="grid h-9 w-9 shrink-0 place-items-center rounded-md text-purple-700 transition hover:bg-purple-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-purple-700">
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
